import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { IconButton, Tooltip } from "@mui/material";
import type { GridRenderCellParams } from "@mui/x-data-grid-premium";
import { useState } from "react";
import { useClientPermissionsContext } from "@entrilia/shared/contexts/ClientPermissionsContext.ts";
import type { OrganizationReportInfo } from "../../../../../api/types/svTypes.ts";
import useUserContext from "../../../../../context/useUserContext.ts";
import { useReportsContext } from "../../../reporting/report-lists/contexts/reports/ReportsContext";
import { useGroupsContext } from "../../../reporting/report-lists/groups/contexts/GroupsContext.ts";
import SharedWithIcons from "../../../reporting/report-lists/reports/grid/SharedWithIcons.tsx";
import type { ReportCompany } from "../../../reporting/sv/components/viewer/common/sharing/ShareReport.types.ts";
import ShareReportGroup from "../../../reporting/sv/components/viewer/common/sharing/ShareReportGroup.tsx";
interface Props {
  // The depth-1 (report-group folder) group node of the org grid tree [clientCode, groupName, reportId].
  params: GridRenderCellParams<OrganizationReportInfo>;
}

export default function OrganizationGroupSharingCell({ params }: Props) {
  const { clients, id: currentUserId } = useUserContext();
  const { groups, actions } = useGroupsContext();
  const { reports } = useReportsContext<OrganizationReportInfo>();
  const { hasAnyPermissionForClient } = useClientPermissionsContext();
  const [open, setOpen] = useState(false);

  const node = params.rowNode;
  if (node.type !== "group") {
    return null;
  }

  const groupCaption = String(node.groupingKey ?? "");

  // carry the empty GUID as groupId
  if (!groupCaption) {
    return null;
  }

  const childIds = new Set(node.children);
  const childRows = reports.filter((r) => childIds.has(r.reportId));

  const clientCode = childRows[0]?.clientCode;
  const loadedGroup = clientCode ? (groups[clientCode] ?? []).find((g) => g.caption === groupCaption) : undefined;
  const groupId = loadedGroup?.id ?? childRows[0]?.groupId;
  const inheritingChild = childRows.find((r) => r.inheritFromParent && (r.authorization?.sharedWith?.length ?? 0) > 0);
  const sharedWith = loadedGroup?.sharedWith ?? inheritingChild?.authorization.sharedWith ?? [];

  if (!clientCode || !groupId) {
    return null;
  }

  const client = clients.find((c) => c.clientCode === clientCode);
  const company: ReportCompany | undefined = client
    ? { clientCode: client.clientCode, name: client.title, logoMarkUrl: client.branding.logoMarkUrl }
    : undefined;

  const canShare = hasAnyPermissionForClient(clientCode, ["ManageAllReports"]);
  const canOpen = canShare && !!company && !!actions.getSharing && !!actions.updateSharing;

  const dialog =
    open && canOpen && company && actions.getSharing && actions.updateSharing ? (
      <ShareReportGroup
        clientCode={clientCode}
        groupId={groupId}
        groupCaption={groupCaption}
        company={company}
        currentUserId={currentUserId}
        getSharing={actions.getSharing}
        updateSharing={actions.updateSharing}
        onClose={() => setOpen(false)}
      />
    ) : null;

  if (sharedWith.length > 0) {
    const tooltip = `Shared with\n${sharedWith.map((e) => e.name).join("\n")}`;
    return (
      <>
        <SharedWithIcons
          sharedWith={sharedWith}
          tooltip={tooltip}
          onClick={canOpen ? () => setOpen(true) : undefined}
        />
        {dialog}
      </>
    );
  }

  if (!canOpen) {
    return null;
  }

  return (
    <>
      <Tooltip title="Share group" enterDelay={500} enterNextDelay={500} arrow disableInteractive>
        <IconButton className="sharing-action" onClick={() => setOpen(true)} sx={{ cursor: "pointer" }}>
          <ShareOutlinedIcon />
        </IconButton>
      </Tooltip>
      {dialog}
    </>
  );
}
